import { ClassFilterType } from "@common/types/class";
import { CatalogItem, CatalogItemPrice } from "@common/types/catalogItem";
import {
    getCurrentActivePrice,
    getCurrentActivePriceForCurrency,
} from "@common/helpers/catalogItemPrice";
import { FiltersTypes } from "@common/types/filters/general";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";

// Header filters held by the page. Class and account filtering are applied by
// the backend.
export type CatalogItemFilters = Pick<
    FiltersTypes,
    | "entityIds"
    | "classes"
    | "classFilterType"
    | "incomeAccountIds"
    | "receivablesAccountIds"
>;

export type CatalogItemClassFilterType = Exclude<
    ClassFilterType,
    ClassFilterType.HAS_CLASSES
>;

// The overlay's match mode as the backend accepts it. "No classes" needs no
// selection; the other modes only apply once classes are picked, so a mode left
// over from an emptied selection is dropped. has_classes is not offered here.
interface ResolveCatalogItemClassFilterTypeParams {
    classFilterType?: ClassFilterType;
    classIds?: string[];
}

export function resolveCatalogItemClassFilterType({
    classFilterType,
    classIds,
}: ResolveCatalogItemClassFilterTypeParams):
    | CatalogItemClassFilterType
    | undefined {
    if (classFilterType === ClassFilterType.NO_CLASSES) {
        return classFilterType;
    }
    if (
        classIds !== undefined &&
        classIds.length > 0 &&
        classFilterType !== undefined &&
        classFilterType !== ClassFilterType.HAS_CLASSES
    ) {
        return classFilterType;
    }
    return undefined;
}

export interface CatalogItemRow extends CatalogItem {
    // Active price for the workspace's default currency, precomputed for display.
    unitPrice: number | null;
    currency: string | null;
    // Retained for display-only cells; the inline placeholder row was removed in
    // favor of the Add item modal, so this is never set anymore.
    isNew?: boolean;
}

export interface CatalogItemsTableCallbacks {
    onEdit: (item: CatalogItemRow) => void;
    onDuplicate: (item: CatalogItemRow) => void;
    onArchiveToggle: (item: CatalogItemRow) => void;
    onDelete: (item: CatalogItemRow) => void;
}

export interface CatalogItemsViewOptions {
    // Narrowed entity-filter selection from the page header. Undefined when the
    // filter spans all business entities (no narrowing).
    selectedEntityIds?: number[];
}

export type CatalogItemsTableMeta = ViewTableMeta<
    CatalogItemsTableCallbacks,
    CatalogItemsViewOptions
>;

function getActiveUnitPrice(item: CatalogItem): {
    unitPrice: number | null;
    currency: string | null;
} {
    const activePrice = getCurrentActivePrice(item.prices);
    return {
        unitPrice: activePrice ? activePrice.unitAmount : null,
        currency: activePrice ? activePrice.currency : null,
    };
}

// Single source of truth for "the price this item would contribute to an
// invoice in the given currency" — used for both the picker's displayed rate
// and the values written on selection so the two can never diverge.
export function getActivePriceForCurrency(
    item: CatalogItem,
    currency: string,
): CatalogItemPrice | null {
    return getCurrentActivePriceForCurrency(item.prices, currency);
}

export function buildCatalogItemRows(items: CatalogItem[]): CatalogItemRow[] {
    return items.map((item) => ({
        ...item,
        ...getActiveUnitPrice(item),
    }));
}
